import { MEDUSA_API_URL, MEDUSA_API_KEY } from './config';

// Capa de servicio del blog. Todo lo que antes vivía en localStorage ahora pasa
// por el backend Medusa:
//   - Lectura pública  → /store/blog/*   (clave publicable)
//   - Escritura admin  → /admin/blog/*   (token de admin en localStorage)
// Las imágenes se suben a S3/R2 vía el endpoint nativo /admin/uploads.

const adminToken = () => (localStorage.getItem('admin_token') || '').trim();

// --- Mapeo snake_case (backend) ↔ camelCase (componentes React) ---
// Claves que tienen columna propia en el backend. Todo lo demás que traiga el
// objeto post (contentWidth, coverImage, y futuros ajustes de layout) va a `meta`.
const CORE_KEYS = new Set([
  'id', 'title', 'slug', 'excerpt', 'status', 'category', 'tags', 'thumbnail',
  'author', 'heroSlider', 'blocks', 'createdAt', 'updatedAt', 'publishedAt', 'meta',
]);

function toClient(p) {
  if (!p) return p;
  return {
    // Los campos extra guardados en meta se exponen al nivel del post (el editor
    // y el render los leen como post.contentWidth, post.coverImage, etc.).
    ...(p.meta || {}),
    id: p.id,
    title: p.title,
    slug: p.slug,
    excerpt: p.excerpt,
    status: p.status,
    category: p.category,
    tags: p.tags || [],
    thumbnail: p.thumbnail || '',
    author: p.author,
    createdAt: p.created_at,
    updatedAt: p.updated_at,
    publishedAt: p.published_at,
    heroSlider: p.hero_slider || { enabled: false, slides: [], autoplay: false, autoplayDelay: 5000 },
    blocks: p.blocks || [],
  };
}

function toServer(data) {
  const out = {};
  if ('title' in data) out.title = data.title;
  if ('slug' in data) out.slug = data.slug;
  if ('excerpt' in data) out.excerpt = data.excerpt;
  if ('status' in data) out.status = data.status;
  if ('category' in data) out.category = data.category;
  if ('tags' in data) out.tags = data.tags;
  if ('thumbnail' in data) out.thumbnail = data.thumbnail;
  if ('author' in data) out.author = data.author;
  if ('heroSlider' in data) out.hero_slider = data.heroSlider;
  if ('blocks' in data) out.blocks = data.blocks;

  // Recoge los campos extra (no-core) en meta. Solo se envía si hay alguno, para
  // no pisar el meta existente en actualizaciones parciales.
  const meta = {};
  for (const k of Object.keys(data)) {
    if (!CORE_KEYS.has(k)) meta[k] = data[k];
  }
  if (Object.keys(meta).length) out.meta = meta;

  return out;
}

async function storeGet(endpoint) {
  const res = await fetch(`${MEDUSA_API_URL}${endpoint}`, {
    headers: { 'x-publishable-api-key': MEDUSA_API_KEY },
  });
  if (!res.ok) throw new Error(`blog store ${res.status}`);
  return res.json();
}

async function adminReq(endpoint, options = {}) {
  const token = adminToken();
  const res = await fetch(`${MEDUSA_API_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!res.ok) throw new Error(`blog admin ${res.status}`);
  return res.json();
}

// ---------- Lectura pública ----------
export async function fetchPublishedPosts(category) {
  const qs = category ? `?category=${encodeURIComponent(category)}` : '';
  const data = await storeGet(`/store/blog/posts${qs}`);
  return (data.posts || []).map(toClient);
}

export async function fetchPostBySlug(slug) {
  const data = await storeGet(`/store/blog/posts/${encodeURIComponent(slug)}`);
  return toClient(data.post);
}

export async function fetchPublicCategories() {
  const data = await storeGet('/store/blog/categories');
  return data.categories || [];
}

export async function fetchSettings() {
  const data = await storeGet('/store/blog/settings');
  return data.settings || {};
}

// ---------- Lectura/escritura admin ----------
export async function fetchAllPosts() {
  const data = await adminReq('/admin/blog/posts');
  return (data.posts || []).map(toClient);
}

export async function createPost(postData) {
  const data = await adminReq('/admin/blog/posts', {
    method: 'POST',
    body: JSON.stringify(toServer(postData)),
  });
  return toClient(data.post);
}

export async function updatePost(id, postData) {
  const data = await adminReq(`/admin/blog/posts/${id}`, {
    method: 'POST',
    body: JSON.stringify(toServer(postData)),
  });
  return toClient(data.post);
}

export async function deletePost(id) {
  await adminReq(`/admin/blog/posts/${id}`, { method: 'DELETE' });
}

export async function fetchAdminCategories() {
  const data = await adminReq('/admin/blog/categories');
  return data.categories || [];
}

export async function createCategory(cat) {
  const data = await adminReq('/admin/blog/categories', {
    method: 'POST',
    body: JSON.stringify(cat),
  });
  return data.category;
}

export async function updateCategory(id, cat) {
  const data = await adminReq(`/admin/blog/categories/${id}`, {
    method: 'POST',
    body: JSON.stringify(cat),
  });
  return data.category;
}

export async function deleteCategory(id) {
  await adminReq(`/admin/blog/categories/${id}`, { method: 'DELETE' });
}

export async function fetchMedia() {
  const data = await adminReq('/admin/blog/media');
  return data.media || [];
}

export async function createMedia(item) {
  const data = await adminReq('/admin/blog/media', {
    method: 'POST',
    body: JSON.stringify(item),
  });
  return data.media;
}

export async function deleteMedia(id) {
  await adminReq(`/admin/blog/media/${id}`, { method: 'DELETE' });
}

export async function saveSettings(settings) {
  const data = await adminReq('/admin/blog/settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
  return data.settings;
}

// Sube un archivo de imagen a S3/R2 mediante el endpoint nativo de Medusa y
// devuelve la URL pública. NO se guarda base64 en ningún sitio.
export async function uploadImage(file) {
  const token = adminToken();
  const form = new FormData();
  form.append('files', file);
  const res = await fetch(`${MEDUSA_API_URL}/admin/uploads`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!res.ok) throw new Error(`upload ${res.status}`);
  const data = await res.json();
  const uploaded = (data.files || data.uploads || [])[0];
  return uploaded?.url;
}
